
  import {useVuelidate} from "@vuelidate/core";
  import CurrencyField from "@/components/CurrencyField.vue";
  import {CashFlow} from "@/model/CashFlow";
  import {OrderMonthlySummariesMonth} from "@/model/OrderMonthlySummaries";
  import {CashFlowType, cashFlowTypeSelectionItems} from "@/enums";
  import StatisticService from "@/service/StatisticService";
  import {defineComponent, reactive, ref, SetupContext} from "vue";
  import {OnCloseCallback, OnSaveCallback} from "@/core/DialogCallbacks";
  import eventBus from "@/eventbus";
  import {MessageTypes} from "@/components/message-types";
  import {ADDING_ENTRY, ENTRY_ADDED, ENTRY_UPDATED, UPDATING_ENTRY, VALUE} from "@/plugins/i18n";
  import {helpers, required} from "@vuelidate/validators";
  import {errorMessage} from "@/ValidationUtils";
  import Order from "@/model/Order";
  import {t, tWithParms} from "@/mixin/mixins";
  import moment from "moment";

  function callbacks() {
    const onSaveCallback = ref<OnSaveCallback<CashFlow>>();
    const onCloseCallback = ref<OnCloseCallback>();
    return {
      onSaveCallback,
      onCloseCallback
    };
  }

  function validation(context: SetupContext, model: CashFlow) {
    const rules = {
      amount: {required: helpers.withMessage(tWithParms("validation.required", t(VALUE)), required)},
      month: {required}
    };

    const v$ = useVuelidate(rules, model);

    return {
      v$
    };
  }

  /**
   * The previous month if it still lacks a cash flow (or, without any cash flows, has worked time),
   * otherwise the current month.
   */
  export function defaultCashFlowMonth(cashFlows: Array<CashFlow>, monthlySummaries: Array<OrderMonthlySummariesMonth>): string {
    const previousMonth = moment().subtract(1, "month").format("YYYY-MM");
    const currentMonth = moment().format("YYYY-MM");

    const usePreviousMonth = cashFlows.length > 0
      ? !cashFlows.some(cashFlow => cashFlow.month === previousMonth)
      : monthlySummaries.some(summary => summary.month === previousMonth && summary.metrics.minutes > 0);

    return usePreviousMonth ? previousMonth : currentMonth;
  }

  export const useDialog =(context: SetupContext) => {
    const isDialogOpened = ref<boolean>(false);
    const isLoading = ref<boolean>(false);
    const title = ref<string>("");
    const orderUuidRef = ref<string>();
    const orderName = ref<string>("");
    const {onSaveCallback, onCloseCallback} = callbacks();

    const model = reactive<CashFlow>({
      month: "",
      amount: 0.00,
      type: CashFlowType.IN
    });

    const createCashFlow = (
      order: Order,
      cashFlows: Array<CashFlow>,
      monthlySummaries: Array<OrderMonthlySummariesMonth>,
      onSave?: OnSaveCallback<CashFlow>,
      onClose?: OnCloseCallback
    ): void => {
      orderUuidRef.value = order.uuid;
      orderName.value = order.name ?? "";
      title.value = t("cashFlow.createCashFlow");

      Object.assign(model, {
        id: undefined,
        month: defaultCashFlowMonth(cashFlows, monthlySummaries),
        amount: 0.00,
        type: CashFlowType.IN,
        description: undefined
      });
      isDialogOpened.value = true;

      onSaveCallback.value = onSave;
      onCloseCallback.value = onClose;
    };

    const editCashFlow = (order: Order, cashFlow: CashFlow, onSave?: OnSaveCallback<CashFlow>, onClose?: OnCloseCallback): void => {
      orderUuidRef.value = order.uuid;
      orderName.value = order.name ?? "";
      title.value = t("cashFlow.editCashFlow");
      Object.assign(model, {description: undefined, ...cashFlow});
      isDialogOpened.value = true;

      onSaveCallback.value = onSave;
      onCloseCallback.value = onClose;
    };

    return {
      createCashFlow,
      editCashFlow,
      isDialogOpened,
      isLoading,
      title,
      model,
      orderName,
      orderUuid: orderUuidRef,
      onSaveCallback,
      onCloseCallback
    };
  };

  export default defineComponent({
    name: "StatisticOrderCashFlowDetailsDialog",
    components: {
      CurrencyField
    },
    setup(props, context) {
      const yearMonthMenu = ref<boolean>(false);

      const {cashFlowTypes} = cashFlowTypeSelectionItems();
      const {
        createCashFlow,
        editCashFlow,
        isDialogOpened,
        isLoading,
        title,
        model,
        orderName,
        orderUuid,
        onSaveCallback,
        onCloseCallback
      } = useDialog(context);

      const {v$} = validation(context, model);
      const cancel = () => {
        isDialogOpened.value = false;
        isLoading.value = false;
        onCloseCallback.value?.call(this);
      };

      const save = () => {
        v$.value.$reset();
        v$.value.$touch();
        if (!v$.value.$invalid) {
          if (model.id) {
            eventBus.$emit(MessageTypes.LOADING, t(UPDATING_ENTRY));
            StatisticService.updateCashFlow(orderUuid.value!, model.id, model)
              .then(response => {
                Object.assign(model, {description: undefined, ...response.data});
                eventBus.$emit(MessageTypes.SUCCESS, t(ENTRY_UPDATED));
                isDialogOpened.value = false;
                isLoading.value = false;
                onSaveCallback.value?.call(this, {...model});
              });
          } else {
            eventBus.$emit(MessageTypes.LOADING, t(ADDING_ENTRY));
            StatisticService.createCashFlow(orderUuid.value!, model)
              .then(response => {
                Object.assign(model, response.data);
                eventBus.$emit(MessageTypes.SUCCESS, t(ENTRY_ADDED));
                isDialogOpened.value = false;
                isLoading.value = false;
                onSaveCallback.value?.call(this, {...model});
              });
          }
        }
      };
      return {
        cancel,
        cashFlowTypes,
        createCashFlow,
        editCashFlow,
        dialog: isDialogOpened,
        loading: isLoading,
        title,
        errorMessage,
        model,
        orderName,
        save,
        yearMonthMenu,
        v$
      };
    }
  });
